import {
  IconClock,
  IconFile,
  IconPaperclip,
  IconPlus,
  IconQuestionMark,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { ActionIcon, Badge, Button, Card, Grid, Group, TagsInput, Text } from '@mantine/core';
import { useParams } from 'react-router';
import SearchAsYouTypeUploadModal from './SearchAsYouTypeUploadModal';

const SearchAsYouTypeSynonymsTab = ({ search, handleSearchChange }) => {
  const [toggleUpload, setToggleUpload] = useState(false);
  const { domainId } = useParams();

  const handleCustomSynonymPairChange = (pair) => {
    if (pair.name === 'newSynonymPair') {
      handleSearchChange({ target: { name: 'customSynonymPairs', value: [...search.customSynonymPairs, pair.value] } });
    }

    if (pair.name === 'removeSynonymPair') {
      handleSearchChange({
        target: { name: 'customSynonymPairs', value: search.customSynonymPairs.filter((p) => p.id !== pair.id) },
      });
    }

    if (pair.name === 'synonymPair') {
      handleSearchChange({
        target: {
          name: 'customSynonymPairs',
          value: search.customSynonymPairs.map((p) => (p.id === pair.id ? { ...p, value: pair.value } : p)),
        },
      });
    }
  };

  // magellan-api uses a pair's id as its Elasticsearch synonym rule id, so an existing pair keeps
  // its id for life and a new one gets a suffix above every numeric suffix already in use.
  const nextCustomSynonymPairId = () => {
    const highestSuffix = search.customSynonymPairs.reduce((highest, pair) => {
      const suffix = /\/(\d+)$/.exec(String(pair.id));
      return suffix ? Math.max(highest, Number(suffix[1])) : highest;
    }, -1);

    return `${domainId}/${highestSuffix + 1}`;
  };

  const addNewCustomSynonymPairButton = (
    <Button
      fullWidth
      onClick={() => {
        handleCustomSynonymPairChange({
          name: 'newSynonymPair',
          value: { id: nextCustomSynonymPairId(), value: [] },
        });
      }}
      variant="default"
      size="sm"
      disabled={search.customSynonymPairs.length > 20}
      leftSection={<IconPlus size={14} />}
    >
      {search.customSynonymPairs.length > 20 ? 'Max Custom Pairs Reached' : 'Add New Custom Synonym Pair'}
    </Button>
  );

  const handleFileClick = (e, file) => {
    e.stopPropagation();
    e.preventDefault();
    window.open(file.url, '_blank');
  };

  const getFileIconStatus = (status) => {
    if (status === 'pending') {
      return <IconClock size={14} color="orange" />;
    }

    if (status === 'synced') {
      return <IconRefresh size={14} color="green" />;
    }

    if (status === 'default') {
      return <IconFile size={14} />;
    }

    return <IconQuestionMark size={14} />;
  };

  const handleRemoveFile = (fileToRemove) => {
    handleSearchChange({
      target: {
        name: 'synonymPairsFiles',
        value: search.synonymPairsFiles.filter((f) => f.label !== fileToRemove.label),
      },
    });
  };

  return (
    <>
      <SearchAsYouTypeUploadModal
        show={toggleUpload}
        close={(synonymFile) => {
          setToggleUpload(false);
          if (synonymFile) {
            handleSearchChange({
              target: { name: 'synonymPairsFiles', value: [...search.synonymPairsFiles, synonymFile] },
            });
          }
        }}
      />
      <Card withBorder>
        <Card.Section p="md">
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 8 }}>
              <label>Synonym pairs files</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <Group gap="xs" mb="xs">
                {search.synonymPairsFiles?.map((file, index) => (
                  <Badge
                    key={index}
                    size="lg"
                    variant="light"
                    rightSection={getFileIconStatus(file.status)}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => handleFileClick(e, file)}
                    pr={3}
                  >
                    <Group gap={4}>
                      <span>{file.label}</span>
                      <ActionIcon
                        size="xs"
                        variant="transparent"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(file);
                        }}
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Group>
                  </Badge>
                ))}
                <Button variant="default" size="xs" onClick={() => setToggleUpload(true)}>
                  <IconPaperclip size={14} />
                </Button>
              </Group>
              <Text size="xs" c="dimmed">
                Choose from predefined synonyms template files or upload your custom one.
                <br />
                You can open your referenced file by clicking on its label.
              </Text>
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 8 }}>
              <label>Custom Synonym Pairs</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              {/* The row position only labels the row for screen readers; every change addresses pair.id. */}
              {search.customSynonymPairs.map((pair, index) => {
                return (
                  <Group key={pair.id} gap="xs" mb="xs">
                    <TagsInput
                      aria-label={`Synonym pair ${index + 1}`}
                      style={{ flex: 1 }}
                      clearable
                      data={[]}
                      value={pair.value}
                      onChange={(synonyms) =>
                        handleCustomSynonymPairChange({
                          id: pair.id,
                          name: 'synonymPair',
                          value: synonyms,
                        })
                      }
                      placeholder="Add new synonyms to this pair..."
                    />
                    <Button
                      aria-label={`Remove synonym pair ${index + 1}`}
                      color="red"
                      variant="filled"
                      onClick={() => handleCustomSynonymPairChange({ id: pair.id, name: 'removeSynonymPair' })}
                    >
                      <IconTrash size={14} />
                    </Button>
                  </Group>
                );
              })}
              {addNewCustomSynonymPairButton}
            </Grid.Col>
          </Grid>
        </Card.Section>
      </Card>
    </>
  );
};

export default SearchAsYouTypeSynonymsTab;
